import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import PropTypes from "prop-types";

import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";

import logolight from "../../assets/images/logo-light.png";
import logodark from "../../assets/images/logo-dark.png";
import callApi from "../../helpers/CallApi";
import { getLogging } from "../../helpers/API";
import withRouter from "../../components/Common/withRouter";
import { DEVELOP_BY, PLATFORM_NAME } from "../../CommonData/Data/constant";

const Login = ({ t }) => {
  const navigate = useNavigate();
  const userRole = ["distributor", "sub-distributor"];
  const [passwordType, setPasswordType] = useState("password");

  const togglePasswordVisibility = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object().shape({
      email: Yup.string()
        .email(t("auth.login.validation.emailInvalid"))
        .required(t("auth.login.validation.emailRequired")),
      password: Yup.string().required(
        t("auth.login.validation.passwordRequired")
      ),
    });
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    values.panel = "distributor";
    await callApi({
      callback: getLogging,
      payload: values,
      onSuccess: ({ tokens, user }) => {
        if (userRole.includes(user.role)) {
          localStorage.setItem("accessToken", tokens?.access?.token);
          localStorage.setItem("accessTokenExpires", tokens?.access?.expires || "");
          localStorage.setItem("refreshToken", tokens?.refresh?.token || "");
          localStorage.setItem("refreshTokenExpires", tokens?.refresh?.expires || "");
          localStorage.setItem("user", JSON.stringify(user));
          navigate("/");
        } else {
          setErrors({ general: t("auth.login.errors.subDistributorOnly") });
          setSubmitting(false);
        }
      },
      onError: (error) => {
        setErrors({ general: (error || t("auth.login.errors.incorrectCredentials")) });
        setSubmitting(false);
      },
    });
  };

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div className="text-center mb-4">
                    <Link to="/">
                      <img
                        src={logodark}
                        alt=""
                        height="24"
                        className="auth-logo logo-dark mx-auto"
                      />
                      <img
                        src={logolight}
                        alt=""
                        height="24"
                        className="auth-logo logo-light mx-auto"
                      />
                    </Link>
                    <h4 className="font-size-18 text-muted mt-2">
                      {t("auth.login.heading.welcome")}
                    </h4>
                    <p className="mb-5">
                      {t("auth.login.heading.signIn", {
                        platformName: PLATFORM_NAME,
                      })}
                    </p>
                  </div>

                  <Formik
                    initialValues={{ email: "", password: "" }}
                    validationSchema={getValidationSchema()}
                    onSubmit={handleSubmit}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      handleBlur,
                      values,
                      errors,
                      touched,
                      isSubmitting,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        {errors.general && (
                          <div className="text-danger text-center mb-3">
                            {errors.general}
                          </div>
                        )}

                        <div className="mb-3">
                          <Label className="form-label">
                            {t("auth.login.labels.email")}
                          </Label>
                          <Input
                            name="email"
                            type="text"
                            placeholder={t(
                              "auth.login.placeholders.enterEmail"
                            )}
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={
                              touched.email && errors.email ? true : false
                            }
                          />
                          {touched.email && errors.email && (
                            <FormFeedback>{errors.email}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">
                            {t("auth.login.labels.password")}
                          </Label>
                          <div className="input-group">
                            <Input
                              name="password"
                              type={passwordType}
                              placeholder={t(
                                "auth.login.placeholders.enterPassword"
                              )}
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={
                                touched.password && errors.password
                                  ? true
                                  : false
                              }
                            />
                            <button
                              type="button"
                              onClick={togglePasswordVisibility}
                              className="btn btn-outline-secondary"
                            >
                              {passwordType === "password"
                                ? t("auth.login.buttons.show")
                                : t("auth.login.buttons.hide")}
                            </button>
                            {touched.password && errors.password && (
                              <FormFeedback>{errors.password}</FormFeedback>
                            )}
                          </div>

                        </div>

                        <Row className="mb-3">
                          <Col xs="6">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="rememberMe"
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="rememberMe"
                              >
                                {t("auth.login.labels.rememberMe")}
                              </Label>
                            </div>
                          </Col>
                          <Col xs="6" className="text-end">
                            <Link to="/auth-recoverpw" className="text-muted">
                              {t("auth.login.links.forgotPassword")}
                            </Link>
                          </Col>
                        </Row>

                        <div className="d-grid">
                          <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {isSubmitting
                              ? t("auth.login.buttons.loggingIn")
                              : t("auth.login.buttons.logIn")}
                          </button>
                        </div>

                        <Row>
                          <Col>
                            <div className="mt-5 text-center">
                              <p className="text-black-50">
                                {t("auth.login.footer.copyright", {
                                  year: new Date().getFullYear(),
                                  platformName: PLATFORM_NAME,
                                })}{" "}
                                <i className="mdi mdi-heart text-danger"></i>{" "}
                                {t("auth.login.footer.developedBy", {
                                  developBy: DEVELOP_BY,
                                })}
                              </p>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(withRouter(Login));

Login.propTypes = {
  history: PropTypes.object,
  t: PropTypes.func.isRequired,
};
